import { useState } from 'react';
import { ATTEMPTS_EYEBROW } from '../constants';
import MarkAsFloater from '../MarkAsFloater';
import type { IdvAttempt, IdvMarkAsPayload } from '../types';
import AttemptCard from './AttemptCard';
import {
  AttemptList,
  AttemptsCount,
  AttemptsEyebrow,
  AttemptsRoot,
  AttemptsTitle,
  EmptyAttemptsNote,
  SectionHeading,
} from './styled';
import type { AttemptsSectionProps } from './types';

const sameId = (a: IdvAttempt['id'] | null | undefined, b: IdvAttempt['id'] | null | undefined): boolean =>
  a != null && b != null && String(a) === String(b);

const AttemptsSection = ({ ops, savingAttemptId = null, onMarkAs }: AttemptsSectionProps) => {
  const [selectedAttemptId, setSelectedAttemptId] = useState<IdvAttempt['id'] | null>(null);
  const selectedAttempt = ops.attempts.find((attempt) => sameId(attempt.id, selectedAttemptId));
  const markAsReason = ops.actions?.mark_as?.reason ?? ops.read_only_reason ?? null;

  const submit = async (payload: IdvMarkAsPayload): Promise<boolean> => {
    const saved = await onMarkAs(payload);
    if (saved) setSelectedAttemptId(null);
    return saved;
  };

  return (
    <AttemptsRoot data-testid="idv-attempts-section">
      <SectionHeading>
        <div>
          <AttemptsEyebrow>{ATTEMPTS_EYEBROW}</AttemptsEyebrow>
          <AttemptsTitle>
            Attempts <AttemptsCount>{ops.attempts.length}</AttemptsCount>
          </AttemptsTitle>
        </div>
      </SectionHeading>
      {ops.attempts.length === 0 && ops.empty_attempts_reason ? (
        <EmptyAttemptsNote data-testid="idv-empty-attempts">{ops.empty_attempts_reason}</EmptyAttemptsNote>
      ) : (
        <AttemptList>
          {ops.attempts.map((attempt) => (
            <AttemptCard
              key={attempt.id}
              attempt={attempt}
              attempts={ops.attempts}
              resolution={ops.resolution}
              markAsReason={markAsReason}
              onMarkAs={setSelectedAttemptId}
            />
          ))}
        </AttemptList>
      )}
      {selectedAttempt ? (
        <MarkAsFloater
          key={selectedAttempt.id}
          attempt={selectedAttempt}
          saving={sameId(savingAttemptId, selectedAttempt.id)}
          onCancel={() => setSelectedAttemptId(null)}
          onSubmit={submit}
        />
      ) : null}
    </AttemptsRoot>
  );
};

export default AttemptsSection;
